/**
 * Discussion Detail Page
 *
 * Story FOS-4.2.6: Master Components & Pages
 * Task 7: Create DiscussionDetailPage
 *
 * Story BL-039.2: Wire DiscussionDetailPage Comments
 * Tasks 4, 5: Wire real API calls for discussion + comments.
 *
 * Shows a single discussion with all collaboration features.
 * Loads discussion data and comments from oracle-bridge REST API.
 *
 * BL-029.4: Migrated from nanostores $user to useAuth().user.
 *
 * @module pages/Governance/DiscussionDetailPage
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@hello-world-dao/auth';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { DiscussionView, type GuidedAnswers, type ContributorInvitation, type QualityGates } from '../../components/governance';
import type { DiscussionStage, RealtimeComment, DiscussionParticipant, DiscussionCategory } from '../../stores/discussionStore';
import {
  getGovernanceDiscussion,
  formatUserIdFallback,
  advanceDiscussionStage,
  listDiscussionContributors,
  inviteDiscussionContributor,
  respondToDiscussionInvitation,
  type GovernanceDiscussion,
  type DiscussionContributorRow,
} from '../../services/discussionService';
import { serviceErrorMessage } from '../../utils/serviceErrorMessage';
import {
  listDiscussionComments,
  postDiscussionComment,
  editDiscussionComment,
  deleteDiscussionComment,
  voteDiscussionComment,
  type DiscussionComment,
} from '../../services/discussionCommentService';

/**
 * Discussion data structure for page state
 */
interface DiscussionData {
  id: string;
  title: string;
  category: DiscussionCategory;
  stage: DiscussionStage;
  stageChangedAt: number;
  proposerId: string;
  proposerName: string;
  comments: RealtimeComment[];
  participants: DiscussionParticipant[];
  gates: QualityGates;
  isGuidedMode: boolean;
  guidedAnswers: GuidedAnswers;
  invitations: ContributorInvitation[];
  submittedProposalId?: number;
}

/**
 * Map a DiscussionComment from oracle-bridge to a RealtimeComment for DiscussionView
 */
function mapToRealtimeComment(c: DiscussionComment): RealtimeComment {
  return {
    id: c.id,
    threadId: c.discussionId,
    content: c.content,
    authorId: c.authorUserId,
    authorName: c.authorDisplayName,
    authorType: { Human: null },
    timestamp: new Date(c.createdAt).getTime(),
    isRetracted: c.isDeleted,
    retractedAt: c.isDeleted ? new Date(c.updatedAt).getTime() : undefined,
    // BUG-DAO-032: Carry vote counts from API response so the UI renders them
    score: c.score,
    upvotes: c.upvotes,
    downvotes: c.downvotes,
  };
}

/**
 * Map GovernanceDiscussion to DiscussionData
 */
function mapToDiscussionData(d: GovernanceDiscussion, comments: RealtimeComment[]): DiscussionData {
  const hoursElapsed = Math.floor((Date.now() - d.createdAt) / (1000 * 60 * 60));

  return {
    id: d.id,
    title: d.title,
    category: (d.category as DiscussionCategory) ?? 'Operational',
    stage: d.stage,
    stageChangedAt: d.updatedAt,
    proposerId: d.createdBy,
    proposerName: d.createdByName || formatUserIdFallback(d.createdBy),
    comments,
    participants: [],
    gates: {
      participants: { current: d.participantCount || 0, required: 3 },
      comments: { current: d.commentCount, required: 5 },
      timeElapsed: { current: hoursElapsed, required: 24 },
    },
    isGuidedMode: false,
    guidedAnswers: {},
    invitations: [],
    submittedProposalId: undefined,
  };
}

/**
 * Build the participants + invitations view state from the contributors API.
 *
 * Participants = the proposer (always) + every ACCEPTED contributor. Pending
 * rows become invitations; declined rows are dropped from the view. (bl-1046)
 */
function buildContributorState(
  proposerId: string,
  proposerName: string,
  rows: DiscussionContributorRow[],
): { participants: DiscussionParticipant[]; invitations: ContributorInvitation[] } {
  const participants: DiscussionParticipant[] = [
    {
      id: proposerId,
      displayName: proposerName,
      isProposer: true,
      isContributor: false,
      joinedAt: 0,
    },
  ];

  const invitations: ContributorInvitation[] = [];

  for (const row of rows) {
    if (row.status === 'accepted') {
      participants.push({
        id: row.userId,
        displayName: formatUserIdFallback(row.userId),
        isProposer: false,
        isContributor: true,
        joinedAt: row.respondedAt ?? row.invitedAt,
      });
    } else if (row.status === 'pending') {
      invitations.push({
        id: row.id,
        principalId: row.userId,
        displayName: formatUserIdFallback(row.userId),
        status: 'pending',
        invitedAt: row.invitedAt,
      });
    }
  }

  return { participants, invitations };
}

/**
 * Discussion detail page component
 */
export function DiscussionDetailPage() {
  const { discussionId } = useParams<{ discussionId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [discussion, setDiscussion] = useState<DiscussionData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [postError, setPostError] = useState<string | null>(null);
  const [typingUsers, _setTypingUsers] = useState<string[]>([]);
  const [isConnected, setIsConnected] = useState(false);

  // Load discussion and comments on mount (AC1, AC2)
  useEffect(() => {
    const loadDiscussion = async () => {
      if (!discussionId) {
        setError('Discussion ID is required');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Fetch discussion data from oracle-bridge (AC1)
        const discussionData = await getGovernanceDiscussion(discussionId);

        // Fetch comments for this discussion (AC2)
        let realtimeComments: RealtimeComment[] = [];
        try {
          const commentsResponse = await listDiscussionComments(discussionId);
          // Flatten threads: top-level comments + their replies
          realtimeComments = commentsResponse.comments.flatMap((thread) => {
            const topLevel = mapToRealtimeComment(thread);
            const replies = (thread.replies || []).map(mapToRealtimeComment);
            return [topLevel, ...replies];
          });
        } catch (commentErr) {
          console.warn('Failed to load discussion comments:', commentErr);
          // Continue with empty comments — discussion data is still valid
        }

        const data = mapToDiscussionData(discussionData, realtimeComments);

        // Load contributors + invitations (non-fatal — the discussion still
        // renders without them). bl-1046.
        try {
          const contribResponse = await listDiscussionContributors(discussionId);
          const { participants, invitations } = buildContributorState(
            data.proposerId,
            data.proposerName,
            contribResponse.contributors,
          );
          data.participants = participants;
          data.invitations = invitations;
        } catch (contribErr) {
          console.warn('Failed to load discussion contributors:', contribErr);
        }

        setDiscussion(data);
        setIsConnected(true);
      } catch (err) {
        console.error('Failed to load discussion:', err);
        // Check if it's a NOT_FOUND error
        // bl-1153-1: routing still keys on `code`; only the copy changes — the
        // server's own sentence wins when it sent one, this page's curated string
        // otherwise.
        if (err && typeof err === 'object' && 'code' in err && (err as { code: string }).code === 'NOT_FOUND') {
          setError(serviceErrorMessage(err) ?? 'Discussion not found');
        } else {
          setError(serviceErrorMessage(err) ?? 'Failed to load discussion');
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadDiscussion();
  }, [discussionId]);

  // Handle posting a comment (AC3)
  const handlePostComment = useCallback(
    async (content: string) => {
      if (!discussion || !user || !discussionId) return;

      setPostError(null);
      try {
        // Call oracle-bridge to persist the comment
        const created = await postDiscussionComment(discussionId, content);

        // Map returned comment to RealtimeComment and append to state
        const newComment = mapToRealtimeComment(created);

        setDiscussion((prev) =>
          prev
            ? {
                ...prev,
                comments: [...prev.comments, newComment],
                gates: {
                  ...prev.gates,
                  comments: {
                    ...prev.gates.comments,
                    current: prev.gates.comments.current + 1,
                  },
                },
              }
            : null
        );
      } catch (err) {
        console.error('Failed to post comment:', err);
        const errCode = err && typeof err === 'object' && 'code' in err
          ? (err as { code: string }).code
          : null;
        if (errCode === 'UNAUTHENTICATED') {
          setPostError(serviceErrorMessage(err) ?? 'You must be logged in to post a comment.');
        } else {
          setPostError(serviceErrorMessage(err) ?? 'Failed to post comment. Please try again.');
        }
        // Re-throw so CommentComposer knows the submission failed (clears submitting state)
        throw err;
      }
    },
    [discussion, user, discussionId]
  );

  // Handle voting on a comment (AC4)
  // BUG-DAO-032: Update local state with server vote counts so score is visible immediately
  const handleVoteComment = useCallback(
    async (commentId: string, vote: 1 | -1) => {
      if (!discussionId) return;

      const result = await voteDiscussionComment(discussionId, commentId, vote);

      // Update the comment's vote counts in local state when server returns counts
      if (result && typeof result.score === 'number') {
        setDiscussion((prev) =>
          prev
            ? {
                ...prev,
                comments: prev.comments.map((c) =>
                  c.id === commentId
                    ? {
                        ...c,
                        score: result.score,
                        upvotes: result.upvotes,
                        downvotes: result.downvotes,
                      }
                    : c
                ),
              }
            : null
        );
      }
    },
    [discussionId]
  );

  // Handle editing a comment (AC5)
  const handleEditComment = useCallback(
    async (commentId: string, content: string) => {
      if (!discussionId) return;

      const updated = await editDiscussionComment(discussionId, commentId, content);

      // Update comment content in local state
      setDiscussion((prev) =>
        prev
          ? {
              ...prev,
              comments: prev.comments.map((c) =>
                c.id === commentId
                  ? { ...c, content: updated.content }
                  : c
              ),
            }
          : null
      );
    },
    [discussionId]
  );

  // Handle deleting a comment (AC6)
  const handleDeleteComment = useCallback(
    async (commentId: string) => {
      if (!discussionId) return;

      await deleteDiscussionComment(discussionId, commentId);

      // Mark comment as retracted (tombstone [deleted])
      setDiscussion((prev) =>
        prev
          ? {
              ...prev,
              comments: prev.comments.map((c) =>
                c.id === commentId
                  ? { ...c, content: '[deleted]', isRetracted: true, retractedAt: Date.now() }
                  : c
              ),
            }
          : null
      );
    },
    [discussionId]
  );

  // Handle typing indicator change (deferred — WebSocket FOS-4.2.9)
  const handleTypingChange = useCallback((_isTyping: boolean) => {
    // Deferred: Send typing indicator via WebSocket (FOS-4.2.9)
  }, []);

  // Refresh contributors + invitations from the backend (bl-1046).
  const refreshContributors = useCallback(async () => {
    if (!discussionId) return;
    try {
      const contribResponse = await listDiscussionContributors(discussionId);
      setDiscussion((prev) => {
        if (!prev) return prev;
        const { participants, invitations } = buildContributorState(
          prev.proposerId,
          prev.proposerName,
          contribResponse.contributors,
        );
        // Keep the participant gate in sync with the accepted-contributor count
        // (proposer + accepted). The backend increments participant_count on
        // accept; mirror that here so the gate updates without a full reload.
        return {
          ...prev,
          participants,
          invitations,
          gates: {
            ...prev.gates,
            participants: { ...prev.gates.participants, current: participants.length },
          },
        };
      });
    } catch (err) {
      console.warn('Failed to refresh contributors:', err);
    }
  }, [discussionId]);

  // Handle stage transition — proposer advances the discussion, gated by the
  // quality-gate thresholds (enforced server-side too). bl-1046.
  const handleStageTransition = useCallback(async () => {
    if (!discussionId) return;
    const result = await advanceDiscussionStage(discussionId);
    setDiscussion((prev) =>
      prev
        ? { ...prev, stage: result.stage, stageChangedAt: result.updatedAt }
        : prev
    );
  }, [discussionId]);

  // Handle guided answer change
  const handleGuidedAnswerChange = useCallback((question: string, answer: string) => {
    setDiscussion((prev) =>
      prev
        ? {
            ...prev,
            guidedAnswers: {
              ...prev.guidedAnswers,
              [question]: answer,
            },
          }
        : null
    );
  }, []);

  // Handle guided mode skip
  const handleGuidedModeSkip = useCallback(() => {
    setDiscussion((prev) =>
      prev
        ? {
            ...prev,
            isGuidedMode: false,
          }
        : null
    );
  }, []);

  // Handle invite contributor — proposer invites by principal id (bl-1046).
  const handleInviteContributor = useCallback(
    async (principalId: string) => {
      if (!discussionId) return;
      await inviteDiscussionContributor(discussionId, principalId);
      await refreshContributors();
    },
    [discussionId, refreshContributors]
  );

  // Handle accept invitation — the invitee accepts their own invite (bl-1046).
  const handleAcceptInvitation = useCallback(
    async (invitationId: string) => {
      if (!discussionId) return;
      await respondToDiscussionInvitation(discussionId, invitationId, 'accept');
      await refreshContributors();
    },
    [discussionId, refreshContributors]
  );

  // Handle decline invitation — the invitee declines their own invite (bl-1046).
  const handleDeclineInvitation = useCallback(
    async (invitationId: string) => {
      if (!discussionId) return;
      await respondToDiscussionInvitation(discussionId, invitationId, 'decline');
      await refreshContributors();
    },
    [discussionId, refreshContributors]
  );

  // Handle back navigation
  const handleBack = useCallback(() => {
    navigate('/discussions');
  }, [navigate]);

  // Determine user roles.
  // Proposer identity is the discussion's created_by = the session user_id, so
  // isProposer keys on user_id. Contributors and invitations, however, are keyed
  // by IC principal per ADR-015 (invites are sent to a principal, and the backend
  // authorizes accept/decline by the session's ic_principal) — so those compare
  // against user?.icPrincipal, NOT user?.userId. A member with no linked II has a
  // null ic_principal and correctly matches neither.
  const isProposer = discussion?.proposerId === user?.userId;
  const isContributor =
    discussion?.participants.some(
      (p) => p.id === user?.icPrincipal && p.isContributor
    ) ?? false;

  // Get pending invitation for current user (keyed by IC principal per ADR-015)
  const pendingInvitationForUser = discussion?.invitations.find(
    (inv) => inv.principalId === user?.icPrincipal && inv.status === 'pending'
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !discussion) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive mb-4">
          {error || 'Discussion not found'}
        </p>
        <button
          onClick={handleBack}
          className="text-primary hover:underline"
        >
          Back to discussions
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Back button */}
      <button
        onClick={handleBack}
        className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to discussions
      </button>

      {/* Comment post error banner */}
      {postError && (
        <div
          className="mb-4 p-3 rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm"
          role="alert"
        >
          {postError}
        </div>
      )}

      {/* Discussion view */}
      <DiscussionView
        discussionId={discussion.id}
        title={discussion.title}
        stage={discussion.stage}
        stageChangedAt={discussion.stageChangedAt}
        category={discussion.category}
        comments={discussion.comments}
        participants={discussion.participants}
        gates={discussion.gates}
        typingUsers={typingUsers}
        isProposer={isProposer}
        isContributor={isContributor}
        isConnected={isConnected}
        isAuthenticated={!!user}
        isGuidedMode={discussion.isGuidedMode}
        guidedAnswers={discussion.guidedAnswers}
        invitations={discussion.invitations}
        pendingInvitationForUser={pendingInvitationForUser}
        onPostComment={handlePostComment}
        onVoteComment={handleVoteComment}
        onEditComment={handleEditComment}
        onDeleteComment={handleDeleteComment}
        currentUserId={user?.userId}
        onTypingChange={handleTypingChange}
        onStageTransition={handleStageTransition}
        onGuidedAnswerChange={handleGuidedAnswerChange}
        onGuidedModeSkip={handleGuidedModeSkip}
        onInviteContributor={handleInviteContributor}
        onAcceptInvitation={handleAcceptInvitation}
        onDeclineInvitation={handleDeclineInvitation}
        submittedProposalId={discussion.submittedProposalId}
      />
    </div>
  );
}

export default DiscussionDetailPage;
